import React, { useContext } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { getImageURL } from 'utils/cloudinary';
import { allowMarketplace } from 'utils/employee';
import {
  excludeTerminatedAccountsForTerminatedEmployees,
  filterActivePretaxAccountsWithFsaType,
} from 'utils/pretaxAccount';
import {
  RecognitionRoutes,
  StoreRoutes,
  ClaimRoutes,
  AccountRoutes,
} from 'utils/routeGenerator';

import TopNavUser from 'components/common/TopNavUser';
import styled from 'styled-components';
import NavigationBar from '@twicapp/ui/components/NavigationBar';
import NavigationItem from '@twicapp/ui/components/NavigationItem';
import { ReactComponent as WalletSvg } from '@twicapp/ui/icons/wallet.svg';
import { ReactComponent as StoreSvg } from '@twicapp/ui/icons/store.svg';
import { ReactComponent as StartSvg } from '@twicapp/ui/icons/star.svg';
import { ReactComponent as DocumentSvg } from '@twicapp/ui/icons/document.svg';
import { SCREEN_WIDTHS } from '@twicapp/ui/constants/breakpoints';

import ProfileContext from 'contexts/ProfileContext';
import PretaxContext from 'contexts/PretaxContext';

const TopNavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.08);
  height: 67px;
  padding: 0 5px;
  background-color: ${({
    theme: {
      colors: {
        background: { primary },
      },
    },
  }) => primary};
  position: sticky;
  top: 0;
  // this needs to be higher to be on top of search input
  z-index: 2;

  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    padding: 0 32px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const DesktopNavigationBar = styled(NavigationBar)`
  display: none;

  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    display: flex;
  }
`;

const LogoSection = styled.div`
  margin-right: 40px;
  height: 100%;
`;

const HomeLink = styled(NavLink)`
  height: 100%;
  display: flex;
  align-items: center;
`;

const Logo = styled.img.attrs({
  alt: 'Company Logo',
  role: 'link',
  'aria-label': 'Redirect back to landing page',
})`
  max-width: 130px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const TopNavigationBar = () => {
  const { employee, company } = useContext(ProfileContext);
  const { pretax_accounts } = useContext(PretaxContext);
  const location = useLocation();
  const pathname = location.pathname;

  const { employee_wallets, is_terminated } = employee;

  const active_employee_wallets = employee_wallets.filter(
    ({ visible_to_employee }: { visible_to_employee: boolean }) =>
      visible_to_employee
  );

  const active_pretax_accounts =
    excludeTerminatedAccountsForTerminatedEmployees({
      pretax_accounts,
      is_terminated,
    }) || [];

  // Only fsa, hsa pretax accounts can have store enabled
  const store_eligible_pretax_accounts = filterActivePretaxAccountsWithFsaType({
    pretax_accounts: active_pretax_accounts,
  });

  const is_marketplace_enabled = allowMarketplace(employee, company);
  const has_active_pretax_accounts =
    store_eligible_pretax_accounts &&
    store_eligible_pretax_accounts?.length > 0;
  const has_active_employee_wallets = active_employee_wallets.length > 0;
  const is_claims_enabled = company.features.reimbursement;
  const is_recognitions_enabled = company.features.recognition;

  const show_store = is_marketplace_enabled || has_active_pretax_accounts;
  const show_claims = is_claims_enabled;
  const show_recognitions = is_recognitions_enabled;

  const selected_keys = [];
  if (location && location.pathname) {
    const split = location.pathname.split('/');
    if (split.length >= 2) {
      selected_keys.push(split[1] || 'explore');
    } else {
      selected_keys.push('explore');
    }
  }

  const showNavBar = ({ employee, company }: $TSFixMe) => {
    if (company.settings.skip_onboarding) {
      return true;
    }
    return employee.is_profile_complete;
  };

  return (
    <TopNavContainer>
      <LeftSection>
        <LogoSection>
          <HomeLink to="/">
            <Logo
              src={getImageURL(company.company_info.logo_url, {
                width: 130,
              })}
            />
          </HomeLink>
        </LogoSection>
        {employee && showNavBar({ employee, company }) && (
          <DesktopNavigationBar>
            {show_store && (
              <NavigationItem
                label="Store"
                icon={StoreSvg}
                to={
                  allowMarketplace(employee, company)
                    ? StoreRoutes.listExplore()
                    : StoreRoutes.detailPretax({
                        pretax_account: 'home',
                      })
                }
                active={pathname.startsWith('/explore')}
              />
            )}
            <NavigationItem
              label="Accounts"
              icon={WalletSvg}
              to={AccountRoutes.list()}
              active={pathname.startsWith('/accounts')}
            />
            {show_claims && (
              <NavigationItem
                label="Claims"
                icon={DocumentSvg}
                to={ClaimRoutes.list()}
                active={pathname.startsWith('/claims')}
              />
            )}
            {show_recognitions && (
              <NavigationItem
                label="Recognition"
                icon={StartSvg}
                to={RecognitionRoutes.list()}
                active={pathname.startsWith('/recognition')}
              />
            )}
          </DesktopNavigationBar>
        )}
      </LeftSection>

      <RightSection>
        <TopNavUser
          employee={employee}
          company={company}
          pathname={location.pathname}
          show_store={show_store}
          has_active_employee_wallets={has_active_employee_wallets}
          active_employee_wallets={active_employee_wallets}
          active_pretax_accounts={store_eligible_pretax_accounts}
          show_claims={show_claims}
          show_recognitions={show_recognitions}
        />
      </RightSection>
    </TopNavContainer>
  );
};

export default TopNavigationBar;
